// frontend/src/pages/dashboard/Taskhooks/useTaskFilters.js

import { useState, useMemo, useCallback } from "react";

export const useTaskFilters = (tasks, authUser, hasPermission, taskStatuses, users, projects, sprints) => {
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    priority: "",
    assignee: "",
    project: "",
    sprint: "",
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      const isAssigned = task.assignedUsers?.some(
        (u) => String(u.id) === String(authUser?.id)
      );
      const isCreator = String(task.created_by) === String(authUser?.id);
      
      if (!hasPermission("task.view.all") && !isAssigned && !isCreator)
        return false;

      const matchesSearch =
        !filters.search ||
        task.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        task.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
        task.assignedUsers?.some((u) =>
          u.name?.toLowerCase().includes(filters.search.toLowerCase())
        );

      const matchesStatus = !filters.status || task.statusObj?.key === filters.status;
      const matchesPriority = !filters.priority || task.priority === filters.priority;
      const matchesAssignee =
        !filters.assignee ||
        task.assignedUsers?.some((u) => String(u.id) === String(filters.assignee));
      const matchesProject =
        !filters.project ||
        String(task.project_id).trim() === String(filters.project).trim();
      const matchesSprint =
        !filters.sprint || String(task.sprint_id) === String(filters.sprint);

      return (
        matchesSearch &&
        matchesStatus &&
        matchesPriority &&
        matchesAssignee &&
        matchesProject &&
        matchesSprint
      );
    });
  }, [tasks, filters, authUser, hasPermission]);

  const resetFilters = useCallback(() => {
    setFilters({
      search: "",
      status: "",
      priority: "",
      assignee: "",
      project: "",
      sprint: "",
    });
  }, []);

  const renderFilterChips = useCallback(() => {
    const chips = [];
    if (filters.search) chips.push(`Search: "${filters.search}"`);
    if (filters.status)
      chips.push(
        `Status: ${taskStatuses.find((s) => s.key === filters.status)?.name || filters.status}`
      );
    if (filters.priority) {
      chips.push(`Priority: ${filters.priority.charAt(0).toUpperCase() + filters.priority.slice(1)}`);
    }
    if (filters.assignee)
      chips.push(
        `Assignee: ${users.find((u) => String(u.id) === filters.assignee)?.name || "Unknown"}`
      );
    if (filters.project)
      chips.push(
        `Project: ${projects.find((p) => String(p.id) === filters.project)?.name || "Unknown"}`
      );
    if (filters.sprint) {
      chips.push(
        `Sprint: ${sprints.find(s => String(s.id) === String(filters.sprint))?.name || 'Unknown'}`
      );
    }
    return chips;
  }, [filters, taskStatuses, users, projects, sprints]);

  const removeFilter = useCallback((chip) => {
    if (chip.startsWith('Search:')) setFilters(prev => ({ ...prev, search: '' }));
    else if (chip.startsWith('Status:')) setFilters(prev => ({ ...prev, status: '' }));
    else if (chip.startsWith('Priority:')) setFilters(prev => ({ ...prev, priority: '' }));
    else if (chip.startsWith('Assignee:')) setFilters(prev => ({ ...prev, assignee: '' }));
    else if (chip.startsWith('Project:')) setFilters(prev => ({ ...prev, project: '' }));
    else if (chip.startsWith('Sprint:')) setFilters(prev => ({ ...prev, sprint: '' }));
  }, []);

  return {
    filters,
    setFilters,
    filtersOpen,
    setFiltersOpen,
    searchOpen,
    setSearchOpen,
    filteredTasks,
    resetFilters,
    renderFilterChips,
    removeFilter,
  };
};